/**
 * Centralized Pusher/Echo Connection Manager
 * Handles connection lifecycle and provides singleton access to Echo instance
 */
import { BACKEND_URL } from '@/config/app';

class PusherService {
  constructor() {
    this.echo = null;
    this.userId = null;
    this.isConnected = false;
    this.channels = new Map(); // Track active channels
    this.listeners = new Map(); // Track active listeners
  }

  /**
   * Connect to Pusher/Echo with user credentials
   * @param {string|number} userId - The current user's ID
   */
  async connect(userId) {
    if (this.isConnected && this.userId === userId) {
      console.log('Pusher already connected for user:', userId);
      return this.echo;
    }

    // Disconnect existing connection if any
    if (this.isConnected) {
      await this.disconnect();
    }

    try {
      console.log('Connecting to Pusher for user:', userId);
      
      // Dynamic import to avoid SSR issues
      const { default: Echo } = await import('laravel-echo');
      const { default: Pusher } = await import('pusher-js');
      
      // Configure Pusher
      Pusher.logToConsole = false;
      
      Pusher.Runtime.createXHR = function () {
        var xhr = new XMLHttpRequest();
        xhr.withCredentials = true;
        return xhr;
      };

      // Initialize Echo
      window.Pusher = Pusher;
      this.echo = new Echo({
        broadcaster: 'pusher',
        key: '7c28aa380f928f493798',
        cluster: 'eu',
        encrypted: true,
        channelAuthorization: {
          endpoint: BACKEND_URL + "api/pusher",
          withCredentials: true,
        },
      });

      this.userId = userId;
      this.isConnected = true;
      
      console.log('Pusher connected successfully for user:', userId);
      return this.echo;
    } catch (error) {
      console.error('Error connecting to Pusher:', error);
      this.isConnected = false;
      this.userId = null;
      throw error;
    }
  }

  /**
   * Disconnect from Pusher/Echo and cleanup all resources
   */
  async disconnect() {
    if (!this.isConnected || !this.echo) {
      console.log('Pusher not connected, nothing to disconnect');
      return;
    }

    try {
      console.log('Disconnecting Pusher for user:', this.userId);

      // Leave all channels
      for (const [channelName, channel] of this.channels) {
        try {
          console.log('Leaving channel:', channelName);
          if (channel && typeof channel.stopListening === 'function') {
            channel.stopListening();
          }
          if (this.echo && typeof this.echo.leave === 'function') {
            this.echo.leave(channelName);
          }
        } catch (error) {
          console.warn('Error leaving channel:', channelName, error);
        }
      }

      // Clear all tracked channels and listeners
      this.channels.clear();
      this.listeners.clear();

      // Disconnect Echo
      if (this.echo && typeof this.echo.disconnect === 'function') {
        this.echo.disconnect();
      }

      // Clean up global references
      if (window.Echo === this.echo) {
        window.Echo = null;
      }

      this.echo = null;
      this.userId = null;
      this.isConnected = false;

      console.log('Pusher disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting Pusher:', error);
      // Force cleanup even if errors occur
      this.echo = null;
      this.userId = null;
      this.isConnected = false;
      this.channels.clear();
      this.listeners.clear();
    }
  }

  /**
   * Get the current Echo instance
   * @returns {Echo|null} The Echo instance or null if not connected
   */
  getEcho() {
    return this.echo;
  }

  /**
   * Check if Pusher is connected
   * @returns {boolean} Connection status
   */
  isConnectedStatus() {
    return this.isConnected;
  }

  /**
   * Get current user ID
   * @returns {string|number|null} Current user ID or null
   */
  getCurrentUserId() {
    return this.userId;
  }

  /**
   * Join a channel and track it for cleanup
   * @param {string} channelName - Name of the channel to join
   * @returns {Channel|null} The channel instance or null
   */
  joinChannel(channelName) {
    if (!this.echo) {
      console.warn('Cannot join channel, Pusher not connected');
      return null;
    }

    try {
      const channel = this.echo.join(channelName);
      this.channels.set(channelName, channel);
      console.log('Joined channel:', channelName);
      return channel;
    } catch (error) {
      console.error('Error joining channel:', channelName, error);
      return null;
    }
  }

  /**
   * Listen to a private channel and track it for cleanup
   * @param {string} channelName - Name of the private channel
   * @returns {Channel|null} The channel instance or null
   */
  privateChannel(channelName) {
    if (!this.echo) {
      console.warn('Cannot access private channel, Pusher not connected');
      return null;
    }

    try {
      const channel = this.echo.private(channelName);
      this.channels.set(channelName, channel);
      console.log('Connected to private channel:', channelName);
      return channel;
    } catch (error) {
      console.error('Error connecting to private channel:', channelName, error);
      return null;
    }
  }

  /**
   * Leave a specific channel
   * @param {string} channelName - Name of the channel to leave
   */
  leaveChannel(channelName) {
    if (!this.echo) {
      return;
    }

    try {
      const channel = this.channels.get(channelName);
      if (channel && typeof channel.stopListening === 'function') {
        channel.stopListening();
      }
      
      this.echo.leave(channelName);
      this.channels.delete(channelName);
      console.log('Left channel:', channelName);
    } catch (error) {
      console.warn('Error leaving channel:', channelName, error);
    }
  }
}

// Create singleton instance
const pusherService = new PusherService();

export default pusherService; 